import React from 'react';
import { _, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { argsToStringifiable } from '../../../helpers/embed/utils';
import { useUiStyles } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type MultiScaleLineChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };
  numberFormatLeft?: string;
  numberFormatRight?: string;
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  setExternalEmbed?: (_json: types.analysis.EmbedChartData) => void;
  customDownloadFormatting?: (_data: types.widgets.TableData) => types.widgets.TableData;
  title?: string;
  inSameScale?: string[];
};

/**
 * Multi scale line chart component that displays data in a line format
 *
 *
 * @param id - Unique identifier for the chart
 * @param data - Data to be displayed in the chart
 * @param fullScreen - Whether the chart is in fullscreen mode
 * @param readOnly - Whether the chart is in read-only mode
 * @param alert - Alert message to display above the chart
 * @param numberFormatLeft - Format for numbers on the left y-axis
 * @param numberFormatRight - Format for numbers on the right y-axis
 * @param exportTitle - Title used when exporting the chart
 * @param exportSubTitle - Subtitle used when exporting the chart
 * @param download - Whether to show download button
 * @param setExternalEmbed - Callback to set external embed data
 * @returns React component displaying a line chart
 */
const MultiScaleLineChart = (args: MultiScaleLineChartProps) => {
  const {
    id,
    data,
    fullScreen,
    readOnly,

    alert,
    numberFormatLeft = '#.##',
    numberFormatRight = '#.##',
    exportTitle,
    exportSubTitle,
    download = true,
    setExternalEmbed,
    customDownloadFormatting = undefined,
    title,
    inSameScale,
  } = args;

  const uiStyles = useUiStyles();
  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));

      if (setExternalEmbed) {
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'multi-scale-line-chart-v5',
          args: argsToStringifiable({
            ...args,
            data,
          }),
        });
      }

      setLocalData(data);
    };
    handleData();
  }, [data]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        gridTemplateRows: `${alert ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}

      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={data}
              fullScreen={fullScreen}
              numberFormatLeft={numberFormatLeft}
              numberFormatRight={numberFormatRight}
              download={download}
              customDownloadFormatting={customDownloadFormatting}
              title={title}
              inSameScale={inSameScale}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default MultiScaleLineChart;
